/**
 * useLeagueMembership — current user's role & permissions in a league.
 * ─────────────────────────────────────────────────────────────────
 * Mirrors the useTeamRole pattern (TTL-cached map keyed by leagueId)
 * but returns league-level membership: role, granular permissions
 * JSONB, and the helper booleans the UI needs for nav/permission
 * gating.
 *
 * Source of truth: GET /api/leagues/:leagueId which the backend
 * scopes to the current user and includes their member_role +
 * permissions_json. If the user isn't a member, the API returns
 * 403 and this hook returns role: null.
 *
 * USAGE
 *   const { role, isManager, can, loading } = useLeagueMembership(leagueId);
 *   if (can('manage_schedule')) { ... }
 *
 * The `can(permKey)` function mirrors backend requireLeaguePermission
 * exactly: managers bypass; viewers always false; administrators
 * checked against the JSONB.
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';

const cache = new Map();   // leagueId -> { role, perms, expiresAt }
const TTL_MS = 60_000;     // 1 minute

/**
 * The 12 canonical permission keys. Kept in sync with backend
 * middleware/leaguePermissions.js LEAGUE_PERMISSION_KEYS and with
 * the schema comment on league_members.permissions_json (migration
 * 074). If you add a new key on the backend, add it here too.
 */
export const LEAGUE_PERMISSION_KEYS = Object.freeze([
  'manage_league_settings',
  'manage_members',
  'manage_teams',
  'manage_divisions',
  'manage_venues',
  'manage_schedule',
  'manage_players',
  'manage_officials',
  'manage_announcements',
  'manage_finances',
  'manage_public_page',
  'view_reports',
]);

/**
 * Human-readable labels for each permission key, used by the
 * Members page permission grid. Keep these short — they sit next
 * to a checkbox.
 */
export const PERMISSION_LABELS = Object.freeze({
  manage_league_settings: 'Manage league settings',
  manage_members:         'Manage members',
  manage_teams:           'Manage teams',
  manage_divisions:       'Manage divisions',
  manage_venues:          'Manage venues',
  manage_schedule:        'Manage schedule',
  manage_players:         'Manage players',
  manage_officials:       'Manage officials',
  manage_announcements:   'Post announcements',
  manage_finances:        'Manage finances',
  manage_public_page:     'Manage public page',
  view_reports:           'View reports',
});

export function useLeagueMembership(leagueId) {
  const [role, setRole]   = useState(null);
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!leagueId) { setLoading(false); return; }

    const cached = cache.get(leagueId);
    if (cached && cached.expiresAt > Date.now()) {
      setRole(cached.role);
      setPerms(cached.perms || {});
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get(`/leagues/${leagueId}`);
      const r = data?.league?.member_role ?? null;
      const p = data?.league?.permissions_json ?? {};
      cache.set(leagueId, { role: r, perms: p, expiresAt: Date.now() + TTL_MS });
      setRole(r);
      setPerms(p);
    } catch {
      // 403 (not a member) or any other error → role null.
      cache.delete(leagueId);
      setRole(null);
      setPerms({});
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { fetch(); }, [fetch]);

  /**
   * can(permKey) — mirrors backend requireLeaguePermission semantics.
   * Manager bypasses; viewer always false; admin checked against
   * permissions JSONB. Unknown keys default to false (safer).
   */
  const can = useCallback((permKey) => {
    if (role === 'manager') return true;
    if (role === 'viewer')  return false;
    if (role === 'administrator') return perms?.[permKey] === true;
    return false;  // not a member
  }, [role, perms]);

  return {
    role,
    perms,
    loading,
    isManager:       role === 'manager',
    isAdministrator: role === 'administrator',
    isViewer:        role === 'viewer',
    isMember:        role !== null,
    can,
    refresh: fetch,
  };
}

/** Drop cached membership for a league (call after role/perm edits). */
export function invalidateLeagueMembershipCache(leagueId) {
  if (leagueId) cache.delete(leagueId);
  else cache.clear();
}
